import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { SearchNavBar, DataTable } from "../../components/ui";
import { AppStrings, logApiErrorToSentry, purposeValEnhancer } from "../../utils";
import { remittanceActions } from "../../state/remittanceSlice";
import { UFO } from "../../../../assets/Icons";
import RecipientDetails from "./RecipientDetails";
import { remittanceApi } from "../../api/remittanceApi";

const RemittanceRecipient: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, recipient } = useAppSelector((state) => state.remittance);

  const [searchedInput, setSearchedInput] = React.useState<string>("");
  const [pagination, setPagination] = React.useState({
    page: 0,
    size: 10,
    ascending: false,
  });

  {
    /* Initial API call and condition when user came to this page */
  }
  useEffect(() => {
    const formData = {
      page: pagination.page,
      size: pagination.size === 0 ? 10 : pagination.size,
      ascending: pagination.ascending,
      filter: searchedInput,
      purposeCode: purposeValEnhancer(calculator?.amountCalculation?.transaction_purpose),
      currency: calculator?.amountCalculation?.dest_currency,
      remitterId: remitter?.selectedRemitter?.userKey,
    };
    dispatch(remittanceActions.fetchRecipientListRequested(formData));
  }, [pagination, searchedInput]);

  useEffect(() => {
    if (!recipient.recipientList.reload) return;
    const formData = {
      page: pagination.page,
      size: pagination.size === 0 ? 10 : pagination.size,
      ascending: pagination.ascending,
      filter: searchedInput,
      purposeCode: purposeValEnhancer(calculator?.amountCalculation?.transaction_purpose),
      currency: calculator?.amountCalculation?.dest_currency,
      remitterId: remitter?.selectedRemitter?.userKey,
    };
    dispatch(remittanceActions.fetchRecipientListRequested(formData));
    dispatch(remittanceActions.setRecipientListState({ reload: false }));
  }, [recipient.recipientList.reload]);

  const onRefreshClick = () => {
    setPagination({
      page: 0,
      size: 10,
      ascending: false,
    });
    setSearchedInput("");
  };

  const fetchRecipientDetails = async (row: any) => {
    try {
      dispatch(
        remittanceActions.setRecipientDetailState({
          loading: { ...remitterState.recipient.recipientDetail.loading, [row.id]: true },
          error: { ...remitterState.recipient.recipientDetail.error, [row.id]: null },
        })
      );

      const response = await remittanceApi.getRecipientDetailByRemitter({
        remitterId: remitter?.selectedRemitter?.userKey,
        recipientId: row.id,
      });

      dispatch(
        remittanceActions.setRecipientDetailState({
          data: { ...remitterState.recipient.recipientDetail.data, [row.id]: response?.data },
          error: { ...remitterState.recipient.recipientDetail.error, [row.id]: null },
        })
      );

      dispatch(
        remittanceActions.setRecipientDetailState({
          loading: { ...remitterState.recipient.recipientDetail.loading, [row.id]: false },
        })
      );
    } catch (err: any) {
      const errorMsg =
        err?.response?.error?.type || err?.response?.error || AppStrings.GENERIC_ERROR;
      // toast.error(errorMsg);
      dispatch(
        remittanceActions.setRecipientDetailState({
          loading: { ...remitterState.recipient.recipientDetail.loading, [row.id]: false },
          error: { ...remitterState.recipient.recipientDetail.error, [row.id]: errorMsg },
        })
      );
      logApiErrorToSentry(err, "fetchRecipientDetails");
    }
  };
  useEffect(() => {
    if (remitterState?.progresStep?.activeStepNumber < 3) {
      navigate("/send/calculator", { replace: true });
    }
  }, [remitterState?.progresStep?.activeStepNumber]);

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const columns = [
    { key: "name", label: "Recipient Name" },
    { key: "bankAccountCountry", label: "Recipient Bank Country" },
    { key: "accNumber", label: "Recipient Ac.No" },
    { key: "type", label: "Recipient type" },
    { key: "residentCountry", label: "Recipient Country" },
  ];

  const recipientList = recipient?.recipientList?.data?.content || [];
  const totalPages = recipient?.recipientList?.data?.totalPages ?? 1;
  const totalElements = recipient?.recipientList?.data?.totalElements ?? 0;
  const loading = recipient?.recipientList?.loading || recipient?.recipientList?.reload;

  return (
    <>
      <div className="max-w-[1440px] w-full mx-auto px-[160px]">
        {/* {loading && <Loader /> } */}
        <ToastContainer autoClose={3000} position="top-right" />

        {/* Nav Bar */}
        <SearchNavBar
          title="Recipient"
          value={searchedInput}
          onChange={setSearchedInput}
          onRefresh={onRefreshClick}
          addButtonTitle={AppStrings.SEARCH_NAV_BAR.RECIPIENT_ADD_BUTTON}
          onAddClick={() => {
            dispatch(remittanceActions.resetAddRecipientState());
            navigate("/send/recipient/add");
          }}
        />

        <DataTable
          data={recipientList}
          columns={columns}
          loading={loading}
          emptyState={(() => (
            <div className="flex flex-col items-center">
              <UFO width={"100"} height={"100"} />
              <p className="font-normal text-sm p-1 text-center">
                {remitterState?.recipient?.recipientList?.error ||
                  AppStrings.RECIPIENT_SCREEN.EMPTY_MSG}
              </p>
            </div>
          ))()}
          onRowClick={fetchRecipientDetails}
          detailRenderer={(row: any) => (
            <RecipientDetails
              recipientData={recipient?.recipientDetail?.data[row.id] || row}
              loading={recipient?.recipientDetail?.loading[row.id] || false}
              error={recipient?.recipientDetail?.error[row.id] || ""}
            />
          )}
          pagination={{
            page: pagination.page,
            size: pagination.size,
            totalElements: totalElements,
            totalPages: totalPages,
            onPageChange: (p) => setPagination((prev) => ({ ...prev, page: p })),
            onSizeChange: (s) => {
              setPagination((prev) => ({ ...prev, size: s, page: 0 }));
            },
          }}
        />
      </div>
    </>
  );
};

export default RemittanceRecipient;
